// The resolved experience, for anything that renders from it (PRD 45).
//
// Reads the module-level store rather than a context, because the sidebar's
// one consumer sits above BusinessProvider in the route tree — see
// businessExperienceStore for why. Components inside the business section get
// the same value through the same hook, so there is one way to ask.

import { useSyncExternalStore } from 'react';
import {
  BusinessExperienceState,
  getBusinessExperienceState,
  subscribeBusinessExperience,
} from '../services/businessExperienceStore';
import { BusinessCardKey, BusinessNavKey } from '../types/businessApi';

export function useBusinessExperience(): BusinessExperienceState {
  return useSyncExternalStore(subscribeBusinessExperience, getBusinessExperienceState);
}

/**
 * Should this nav row be shown?
 *
 * UNKNOWN MEANS SHOW. Until the experience resolves — and everywhere outside
 * live business mode, where there is no business to have one — every row
 * renders, which is exactly today's behaviour. A menu that hides rows while it
 * waits for a fetch would flicker on every page load, and a menu that hides
 * rows because a fetch FAILED would be a support call about missing features.
 */
export function useNavKeyVisible(): (key: BusinessNavKey | undefined) => boolean {
  const { experience } = useBusinessExperience();
  return (key) => {
    if (!key || !experience) return true;
    if (experience.nav_expanded) return true;
    return experience.nav_keys.includes(key);
  };
}

/** The ordered card set, or null while unknown (draw the default order). */
export function useExperienceCardKeys(): BusinessCardKey[] | null {
  const { experience } = useBusinessExperience();
  return experience?.card_keys ?? null;
}

/**
 * Has the api named this nav key? For a NEW module screen the web ships ahead
 * of (PRD 100 Wave 4's Job profitability): UNKNOWN MEANS HIDE, the opposite of
 * useNavKeyVisible, so nothing new draws until the api that serves it is live.
 * "Show all" sends the whole vocabulary, so it counts too.
 */
export function useServerListsNavKey(key: BusinessNavKey): boolean {
  const { experience } = useBusinessExperience();
  return experience?.nav_keys.includes(key) ?? false;
}
